import UploadIcon from 'Assets/Icons/upload.svg';
import { ErrorDisplay, LoadingIndicator, Render } from 'Components/Display';
import {
  FormActionTypes,
  getFieldValue,
  useFormState,
  useFormUpdater,
  useToasts,
} from 'Context';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond/dist/filepond.min.css';
import { InputType } from 'Models';
import { METHODS } from 'Models/Templates/Routes/Routes';
import React, { useEffect, useRef, useState } from 'react';
import { FilePond, FilePondProps, registerPlugin } from 'react-filepond';
import { buildEndpoint, getPropertyByString, useApiWorker } from 'Utilities';
import { v4 as uuidv4 } from 'uuid';
import './TableFileUploader.scss';

export type RenderAdditionalFormComponentsProp = (
  handleFileFieldChange: (e: any, type: InputType, fieldName: string) => void,
  formatFieldName: (name: string) => string
) => React.ReactElement;

type TableFileUploaderProps = FilePondProps & {
  fileEndpoint: string;
  name?: string;
  isEdit?: boolean;
  withComments?: boolean;
  multipleUploaders?: boolean;
  label: string;
  addOrEditEndpoint?: string;
  fileUploaderDisabledLabel?: string;
  renderAdditionalFormComponents?: RenderAdditionalFormComponentsProp;
  uploadExtraProps?: { [key: string]: any };
};

export const TableFileUploader: React.FC<TableFileUploaderProps> = ({
  fileEndpoint,
  name = 'files',
  label,
  required,
  allowMultiple,
  isEdit,
  withComments,
  multipleUploaders,
  addOrEditEndpoint,
  renderAdditionalFormComponents,
  fileUploaderDisabledLabel,
  uploadExtraProps,
  ...filePondProps
}) => {
  const { formValues, formErrors } = useFormState();
  const sendUpdate = useFormUpdater();
  const API = useApiWorker();
  var filePondRef = useRef<FilePond | null>(null);
  const [hasFiles, setHasFiles] = useState<boolean>(false);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const uploadUrl = buildEndpoint(fileEndpoint, METHODS.Upload);
  const toast = useToasts();

  registerPlugin(FilePondPluginFileValidateType);

  useEffect(() => {
    var hasValues =
      (allowMultiple && getPropertyByString(formValues, name)?.length > 0) ||
      (multipleUploaders && formValues?.[name]?.fileId?.length > 0) ||
      (!allowMultiple && formValues?.fileId?.length > 0);

    if (hasFiles !== hasValues) {
      setHasFiles(hasValues);
    }
  }, [formValues]);

  useEffect(() => {
    const fileErrorValues = formErrors[formatFieldName('fileId')];
    setFileErrors(fileErrorValues);
  }, [formErrors]);

  useEffect(() => {
    if (filePondRef && !hasFiles) {
      filePondRef.current?.removeFiles();
    }
  }, [hasFiles]);

  const formatFieldName = (fieldName: string) => {
    return (
      allowMultiple
        ? `${name}[0].${fieldName}`
        : multipleUploaders
        ? `${name}.${fieldName}`
        : fieldName
    ).toLocaleLowerCase();
  };

  const allowedExtensions = [
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'csv',
    'txt',
    'jpg',
    'jpeg',
    'png',
    'gif',
    'mp4',
    'mov',
  ];
  const MAX_FILES = 10;
  const MAX_FILE_SIZE = 500 * 1000 * 1000;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    if (fileArray.length > MAX_FILES) {
      toast.error(`Only ${MAX_FILES} files can be uploaded at a time`);
      return;
    }

    setLoading(true);
    let uploadedAny = false;

    for (const file of fileArray) {
      const ext = file.name.split('.').pop()?.toLowerCase();

      if (!ext || !allowedExtensions.includes(ext)) {
        toast.error(`${file.name} has an invalid file type`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds 500 MB file size limit`);
        continue;
      }

      const fileId = uuidv4();
      const CHUNK_SIZE = 10 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const formData = new FormData();
        formData.append('contentType', file.type);
        formData.append('fileName', file.name);
        formData.append('chunk', `${i}`);
        formData.append('chunks', `${totalChunks}`);
        formData.append('id', fileId);
        formData.append('file', chunk);

        try {
          await API.post(uploadUrl, formData);
        } catch (err) {
          toast.error(`Failed to upload chunk ${i + 1} of ${file.name}`);
          continue;
        }
      }

      const fileData = {
        fileId,
        fileName: file.name,
        size: file.size,
      };

      if (allowMultiple) {
        const current = getFieldValue(formValues, name) || [];
        sendUpdate(FormActionTypes.UpdateValue, {
          field: name,
          value: [...current, fileData],
        });
      } else {
        sendUpdate(FormActionTypes.SetValues, {
          ...formValues,
          ...{ [name]: fileData },
        });
      }

      if (addOrEditEndpoint) {
        try {
          const payload = {
            referralId: formValues?.referralId,
            title: fileData.fileName,
            ...fileData,
            ...uploadExtraProps,
          };
          await API.post(addOrEditEndpoint, payload);
        } catch (err) {
          toast.error(
            `Upload of ${file.name} succeeded, but submission failed`
          );
        }
      }

      uploadedAny = true;
    }

    if (uploadedAny) {
      toast.success('All valid files uploaded successfully');
      setTimeout(() => window.location.reload(), 1000);
    }

    setLoading(false);
  };

  return (
    <div className="v3-file-uploader">
      <Render condition={!isEdit}>
        <div className="tooltip-wrapper">
          <label
            htmlFor="custom-file-upload"
            className={`upload-label ${
              fileUploaderDisabledLabel ? 'disabled' : ''
            }`}
          >
            Upload <img src={UploadIcon} alt="upload icon" />
          </label>
          {!!fileUploaderDisabledLabel ? null : (
            <input
              id="custom-file-upload"
              disabled={!!fileUploaderDisabledLabel}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.gif,.mp4,.mov"
              multiple={allowMultiple}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          )}

          {fileUploaderDisabledLabel && (
            <div className="tooltip-message">{fileUploaderDisabledLabel}</div>
          )}
        </div>
        <ErrorDisplay errors={fileErrors} />
      </Render>
      {loading && (
        <div className="filepond-overlay">
          <LoadingIndicator />
        </div>
      )}
    </div>
  );
};
